import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { PageHeader, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchCrsfnRelators } from '@/services/bacen';
import type { CrsfnCountRow } from '@/services/types';

/**
 * Who reported the linked acordaos: the caseload per relator at or above the
 * floor, alphabetical, with the appeal-type mix BESIDE the disposition mix
 * and never divided one by the other. A relator who reports mostly ex-officio
 * reviews of acquittals upholds them by construction; the mix says what each
 * docket contained, never how strict a member is.
 */
export function CrsfnRelatorsPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'crsfn', 'relators'],
    queryFn: fetchCrsfnRelators,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const mixText = (rows: CrsfnCountRow[], group: string) =>
    rows.map((r) => `${label(group, r.code)} ${number.format(r.acordaos)}`).join(' · ');

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:crsfnRelators" exportPath="/api/v1/bacen/export/crsfn/relators" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-bench>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnRelators.bench.title')}
        </h2>
        <Answer answer={data.bench} help={t('bacen:crsfnRelators.bench.help')}>
          {(bench) => (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground" data-bench-summary>
                {t('bacen:crsfnRelators.bench.summary', {
                  people: number.format(bench.people),
                  listed: number.format(bench.relators.length),
                  threshold: number.format(bench.threshold),
                  withRelator: number.format(bench.acordaos_with_relator),
                  linked: number.format(bench.acordaos_linked),
                  share: percent.format(bench.attribution_share),
                })}
              </p>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnRelators.bench.relator')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnRelators.bench.acordaos')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnRelators.bench.withDispositive')}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnRelators.bench.appealTypes')}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnRelators.bench.dispositions')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnRelators.bench.overruled')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bench.relators.map((r) => (
                      <tr key={r.slug} className="border-t align-top" data-relator={r.slug}>
                        <td className="px-3 py-2 text-foreground">
                          <Link
                            to={`/bacen/crsfn/r/${r.slug}`}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {r.name}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {r.first_year ?? '—'}–{r.last_year ?? '—'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(r.acordaos)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(r.with_dispositive)}
                          <span className="ml-1 text-xs">
                            ({percent.format(r.dispositive_share)})
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {mixText(r.appeal_types, 'appealType')}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {mixText(r.dispositions, 'disposition')}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(r.overruled)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bench.suppressed > 0 ? (
                <p className="text-xs text-muted-foreground" data-suppressed>
                  {t('bacen:crsfnRelators.bench.suppressed', {
                    n: number.format(bench.suppressed),
                    threshold: number.format(bench.threshold),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:crsfnRelators.guide.bench" />
        </div>
      </section>
    </div>
  );
}
